import { defineStore } from "pinia";
import { computed, markRaw, ref } from "vue";

import type { ProcessElementType } from "@/lib/openapi/gen";

/**
 * What the drawer needs from the BPMN canvas it was opened on.
 *
 * A process element's NAME lives in the BPMN XML, not in the database: the
 * backend's sync_elements() re-stamps process_element.name from the diagram's
 * `name` attribute on every definition sync, and bpmn-js draws the label from
 * the same place. So a rename typed in the drawer has to go through the
 * modeler — `update_task(name=…)` would neither move the label nor survive the
 * next canvas edit.
 *
 * The drawer is mounted in ApplicationLayout (it is a flex child of the app
 * layout, like ThreadDrawer and DomainObjectDrawer) and so has no reference to
 * the canvas component. The canvas registers this handle instead — the same
 * shape of module-level indirection entity-focus.ts and application-focus.ts
 * already use for BPMN state that outlives the diagram component.
 */
export interface ProcessElementCanvasHandle {
    /** Rename the element in the BPMN itself (business object + label). */
    rename: (bpmnId: string, name: string) => void;
}

export interface OpenProcessElementArgs {
    /** ProcessElement identity — stable across diagram reloads. */
    processElementId: string;
    /** The element's id in the BPMN XML. */
    bpmnId: string;
    elementType: ProcessElementType;
    /** The name the BPMN currently carries, which is the authoritative one. */
    name: string;
}

/**
 * Open/closed state for the process-element quick-view/quick-edit panel
 * (ProcessElementDrawer.vue), opened by clicking an element on the BPMN canvas.
 *
 * Deliberately holds only ids, never the bpmn-js element object: loadBpmn()
 * re-imports the XML and recreates every element instance, so a held element
 * goes stale on the first autosave round trip.
 */
export const useProcessElementDrawerStore = defineStore(
    "processElementDrawer",
    () => {
        const drawerOpen = ref(false);
        const drawerElementId = ref<string | undefined>(undefined);
        const drawerBpmnId = ref<string | undefined>(undefined);
        const drawerElementType = ref<ProcessElementType | undefined>(
            undefined
        );

        /**
         * The name as the BPMN itself currently has it.
         *
         * Not read from the process-elements store: that list only catches up
         * after a full definition sync round trip, so between an inline
         * direct-edit rename and that sync it would show the old name in the
         * drawer while the canvas already shows the new one.
         */
        const drawerElementName = ref("");

        const canvas = ref<ProcessElementCanvasHandle | undefined>(undefined);

        /** Whether the last close plays the panel's leave animation. */
        const drawerLeaveAnimated = ref(true);

        /** Whether a rename can be performed at all (a modeler is attached). */
        const canRename = computed(() => !!canvas.value);

        const openDrawer = ({
            processElementId,
            bpmnId,
            elementType,
            name,
        }: OpenProcessElementArgs) => {
            drawerElementId.value = processElementId;
            drawerBpmnId.value = bpmnId;
            drawerElementType.value = elementType;
            drawerElementName.value = name;
            drawerOpen.value = true;
        };

        /**
         * Clears the ids as well as the open flag, for the same reason
         * DomainObjectDrawer's store does: the panel resolves its id against a
         * PROCESS-scoped element list, so a retained id would go on pointing at
         * an element that had since left that list.
         */
        const closeDrawer = ({
            animate = true,
        }: { animate?: boolean } = {}) => {
            drawerLeaveAnimated.value = animate;
            drawerOpen.value = false;
            drawerElementId.value = undefined;
            drawerBpmnId.value = undefined;
            drawerElementType.value = undefined;
            drawerElementName.value = "";
        };

        /**
         * Report the BPMN's current name for an element. Ignored for anything
         * but the element the drawer is open on, so a canvas-wide sweep after
         * commandStack.changed can call it for every element it walks.
         */
        const setElementName = (bpmnId: string, name: string) => {
            if (bpmnId && bpmnId === drawerBpmnId.value) {
                drawerElementName.value = name;
            }
        };

        const registerCanvas = (handle: ProcessElementCanvasHandle) => {
            canvas.value = markRaw(handle);
        };

        /**
         * Identity-checked: two diagram instances can be mounted at once (the
         * page's own canvas and, on ProcessDefinitionPage, the historical one),
         * and the one unmounting must not clear a handle it never registered.
         *
         * Does NOT close the panel. Leaving edit mode tears the modeler down
         * and so unregisters, but the panel is perfectly valid without one —
         * it just stops offering the name field. Closing here would make the
         * Edit/View toggle dismiss a panel the user had open, which no other
         * right drawer in the app does.
         */
        const unregisterCanvas = (handle: ProcessElementCanvasHandle) => {
            if (canvas.value !== handle) return;
            canvas.value = undefined;
        };

        /**
         * Apply a rename to the BPMN. No-op without a modeler attached.
         *
         * `bpmnId` defaults to the open element but is explicit for the panel's
         * flush-on-close/switch paths: those can run after the store has
         * already moved to another element, and the typed name still belongs to
         * the one it was typed against.
         */
        const renameElement = (name: string, bpmnId = drawerBpmnId.value) => {
            if (!bpmnId || !canvas.value) return;
            canvas.value.rename(bpmnId, name);
            // Only the open element's displayed name may advance — renaming an
            // element the panel has already left must not relabel the new one.
            if (bpmnId === drawerBpmnId.value) {
                drawerElementName.value = name;
            }
        };

        return {
            drawerOpen,
            drawerElementId,
            drawerBpmnId,
            drawerElementType,
            drawerElementName,
            drawerLeaveAnimated,
            canRename,
            openDrawer,
            closeDrawer,
            setElementName,
            registerCanvas,
            unregisterCanvas,
            renameElement,
        };
    }
);
